const roundMoney = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const coerceCents = (value) => {
  const cents = Number(value);
  if (!Number.isFinite(cents) || cents < 0) return 0;
  return Math.round(cents);
};

export const allocatePartialPaymentQuoteFromBase = ({
  basePaymentAmount,
  baseAmountRemaining,
  feeAmountRemaining,
}) => {
  const basePaymentAmountCents = Math.max(0, Math.round(Number(basePaymentAmount || 0) * 100));
  const baseAmountRemainingCents = Math.max(0, Math.round(Number(baseAmountRemaining || 0) * 100));
  const feeAmountRemainingCents = Math.max(0, Math.round(Number(feeAmountRemaining || 0) * 100));

  if (basePaymentAmountCents <= 0 || baseAmountRemainingCents <= 0) {
    return { baseAmount: 0, feeAmount: 0, totalAmount: 0 };
  }

  if (basePaymentAmountCents >= baseAmountRemainingCents) {
    return {
      baseAmount: roundMoney(baseAmountRemainingCents / 100),
      feeAmount: roundMoney(feeAmountRemainingCents / 100),
      totalAmount: roundMoney((baseAmountRemainingCents + feeAmountRemainingCents) / 100),
    };
  }

  const proportionalFeeCents = Math.round(
    (basePaymentAmountCents * feeAmountRemainingCents) / baseAmountRemainingCents
  );
  const baseAmountCents = Math.min(basePaymentAmountCents, baseAmountRemainingCents);
  const feeAmountCents = Math.min(Math.max(0, proportionalFeeCents), feeAmountRemainingCents);
  return {
    baseAmount: roundMoney(baseAmountCents / 100),
    feeAmount: roundMoney(feeAmountCents / 100),
    totalAmount: roundMoney((baseAmountCents + feeAmountCents) / 100),
  };
};

export const resolveWebPaymentQuote = ({
  partialPaymentEnabled,
  requestedBaseAmount,
  remainingBaseAmount,
  remainingFeeAmount,
  waiveFee,
}) => {
  const baseRemaining = roundMoney(Math.max(0, Number(remainingBaseAmount || 0)));
  const feeRemaining = waiveFee ? 0 : roundMoney(Math.max(0, Number(remainingFeeAmount || 0)));
  if (!partialPaymentEnabled) {
    return {
      baseAmount: baseRemaining,
      feeAmount: feeRemaining,
      totalAmount: roundMoney(baseRemaining + feeRemaining),
    };
  }
  return allocatePartialPaymentQuoteFromBase({
    basePaymentAmount: requestedBaseAmount,
    baseAmountRemaining: baseRemaining,
    feeAmountRemaining: feeRemaining,
  });
};

export const resolveFailedPayerAmounts = ({
  hasFailedPayment,
  totalAmount,
  paidAmount,
  remainingBaseAmount,
  remainingFeeAmount,
  failedBaseAmount,
  failedFeeAmount,
  failedAmount,
}) => {
  const normalBaseRemaining = roundMoney(Math.max(0, Number(remainingBaseAmount || 0)));
  const normalFeeRemaining = roundMoney(Math.max(0, Number(remainingFeeAmount || 0)));
  const normalRemaining = roundMoney(normalBaseRemaining + normalFeeRemaining);
  const normalizedPaid = roundMoney(Math.max(0, Number(paidAmount || 0)));
  if (!hasFailedPayment) {
    return {
      paidAmount: normalizedPaid,
      remainingBaseAmount: normalBaseRemaining,
      remainingFeeAmount: normalFeeRemaining,
      remainingAmount: normalRemaining,
    };
  }

  const normalizedTotal = roundMoney(Math.max(0, Number(totalAmount || 0)));
  const explicitFailedTotal = roundMoney(Math.max(0, Number(failedAmount || 0)));
  const failedOutstanding = Math.max(
    normalRemaining,
    explicitFailedTotal,
    normalRemaining <= 0.0001 && explicitFailedTotal <= 0.0001 ? normalizedTotal : 0
  );
  const explicitFailedBase = roundMoney(Math.max(0, Number(failedBaseAmount || 0)));
  const explicitFailedFee = roundMoney(Math.max(0, Number(failedFeeAmount || 0)));
  const failedBreakdownTotal = roundMoney(explicitFailedBase + explicitFailedFee);
  const recoveryBase = failedBreakdownTotal > 0.0001
    ? roundMoney(Math.min(failedOutstanding, explicitFailedBase))
    : roundMoney(Math.min(failedOutstanding, normalBaseRemaining || failedOutstanding));
  const recoveryFee = roundMoney(Math.max(0, failedOutstanding - recoveryBase));

  return {
    paidAmount: roundMoney(Math.max(0, normalizedTotal - failedOutstanding)),
    remainingBaseAmount: recoveryBase,
    remainingFeeAmount: recoveryFee,
    remainingAmount: failedOutstanding,
  };
};

export const resolvePayerParticipantDisplayName = ({ isViewer, resolvedName }) => (
  isViewer ? 'You' : String(resolvedName || '').trim()
);

export const resolveOrganizerParticipantAmount = (session) => {
  const canonicalAmount = Number(session?.organizerBaseAmount);
  if (session?.organizerBaseAmount !== undefined && Number.isFinite(canonicalAmount)) {
    return Math.max(0, roundMoney(canonicalAmount));
  }
  const legacyAmount = Number(session?.perPerson);
  return Number.isFinite(legacyAmount) ? Math.max(0, roundMoney(legacyAmount)) : 0;
};

export const shouldShowOrganizerParticipant = (organizerAmount) => (
  Number(organizerAmount || 0) > 0.0001
);

export const shouldShowPayerPaymentFooter = ({
  paid,
  inviteStatus,
  isPaymentFullyCoveredByProcessing,
}) => {
  const normStatus = String(inviteStatus || '').trim().toLowerCase();
  return (
    !paid
    && normStatus !== 'declined'
    && normStatus !== 'paid'
    && normStatus !== 'verified'
    && normStatus !== 'settled_offline'
    && !isPaymentFullyCoveredByProcessing
  );
};

export const finalizePaymentBankReconnect = async ({
  context,
  exchangeResult,
  hasPaymentAuthorization,
  phone,
  registerPaymentBank,
  relinkPaymentBank,
  onRegistrationWarning,
}) => {
  const sourceAccountId = String(context?.sourceAccountId || '').trim();
  if (!context || !hasPaymentAuthorization || !sourceAccountId) return;

  const newAccountId = String(exchangeResult?.accountId || '').trim();
  let registrationError = null;
  try {
    await registerPaymentBank({ phone, plaidAccountId: sourceAccountId });
  } catch (error) {
    registrationError = error;
    onRegistrationWarning?.(error);
  }

  if (newAccountId && newAccountId !== sourceAccountId) {
    await relinkPaymentBank({
      phone,
      oldPlaidAccountId: sourceAccountId,
      newPlaidAccountId: newAccountId,
    });
  } else if (registrationError) {
    throw registrationError;
  }
};

export const getMaxBaseAmountForTotalLimit = ({
  baseAmountRemaining,
  feeAmountRemaining,
  totalLimitCents,
  waiveFee,
}) => {
  const baseRemainingCents = Math.max(0, Math.round(Number(baseAmountRemaining || 0) * 100));
  const limitCents = Math.max(0, Math.round(Number(totalLimitCents || 0)));
  if (baseRemainingCents <= 0 || limitCents <= 0) return 0;

  let low = 0;
  let high = Math.min(baseRemainingCents, limitCents);
  while (low < high) {
    const candidate = Math.ceil((low + high) / 2);
    const quote = resolveWebPaymentQuote({
      partialPaymentEnabled: true,
      requestedBaseAmount: candidate / 100,
      remainingBaseAmount: baseRemainingCents / 100,
      remainingFeeAmount: feeAmountRemaining,
      waiveFee,
    });
    if (Math.round(quote.totalAmount * 100) <= limitCents) low = candidate;
    else high = candidate - 1;
  }
  return roundMoney(low / 100);
};

const parseFeatureBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  const normalized = String(value ?? '').trim().toLowerCase();
  if (['1', 'true', 'yes', 'on', 'enabled'].includes(normalized)) return true;
  if (['0', 'false', 'no', 'off', 'disabled'].includes(normalized)) return false;
  return null;
};

export const resolveCleroPlusEnabled = (data) => {
  if (!data || typeof data !== 'object') return false;
  for (const candidate of [data.cleroPlusEnabled, data.clero_plus_enabled, data.cleroPlus?.enabled]) {
    const parsed = parseFeatureBoolean(candidate);
    if (parsed !== null) return parsed;
  }
  return false;
};

export const getClientPaymentLimitCents = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') return 0;
  const hasExplicit = snapshot.currentUserAvailablePaymentLimitCents !== undefined
    && snapshot.currentUserAvailablePaymentLimitCents !== null;
  const explicit = coerceCents(snapshot.currentUserAvailablePaymentLimitCents);
  const daily = snapshot.daily?.remainingCents !== undefined ? coerceCents(snapshot.daily.remainingCents) : Infinity;
  const weekly = snapshot.weekly?.remainingCents !== undefined ? coerceCents(snapshot.weekly.remainingCents) : Infinity;
  const monthly = snapshot.monthly?.remainingCents !== undefined ? coerceCents(snapshot.monthly.remainingCents) : Infinity;
  const single = snapshot.singlePaymentLimitCents !== undefined ? coerceCents(snapshot.singlePaymentLimitCents) : Infinity;
  const derived = Math.min(single, daily, weekly, monthly);
  if (hasExplicit && Number.isFinite(derived)) return Math.min(explicit, derived);
  if (hasExplicit) return explicit;
  return Number.isFinite(derived) ? derived : 0;
};

const timestampToMillis = (value) => {
  if (!value) return null;
  if (typeof value.toMillis === 'function') return Number(value.toMillis());
  if (typeof value.toDate === 'function') return Number(value.toDate()?.getTime?.());
  if (typeof value.seconds === 'number') return (value.seconds * 1000) + Math.round(Number(value.nanoseconds || 0) / 1_000_000);
  if (typeof value._seconds === 'number') return (value._seconds * 1000) + Math.round(Number(value._nanoseconds || 0) / 1_000_000);
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? null : parsed;
};

export const hasActiveCleroPlusAccess = (subscription, now = Date.now()) => {
  if (String(subscription?.tier || '').trim().toLowerCase() !== 'clero_plus') return false;
  const trialEndsAt = timestampToMillis(subscription?.trialEndsAt);
  const expiresAt = timestampToMillis(subscription?.expiresAt);
  return (trialEndsAt !== null && trialEndsAt > now) || (expiresAt !== null && expiresAt > now);
};
